import { openDB } from 'idb';

const DB_NAME = 'vitality-tracker';
const DB_VERSION = 1;

export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Settings store
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings', { keyPath: 'id' });
      }
      
      // Daily logs store
      if (!db.objectStoreNames.contains('dailyLogs')) {
        const dailyStore = db.createObjectStore('dailyLogs', { keyPath: 'date' });
        dailyStore.createIndex('by-date', 'date');
      }
      
      // Workout logs store
      if (!db.objectStoreNames.contains('workoutLogs')) {
        const workoutStore = db.createObjectStore('workoutLogs', { keyPath: 'id' });
        workoutStore.createIndex('by-date', 'date');
      }
      
      // Meal logs store
      if (!db.objectStoreNames.contains('mealLogs')) {
        const mealStore = db.createObjectStore('mealLogs', { keyPath: 'id' });
        mealStore.createIndex('by-date', 'date');
      }
      
      // Supplement logs store
      if (!db.objectStoreNames.contains('supplementLogs')) {
        const suppStore = db.createObjectStore('supplementLogs', { keyPath: 'id' });
        suppStore.createIndex('by-date', 'date');
      }
      
      // Weight logs store
      if (!db.objectStoreNames.contains('weightLogs')) {
        const weightStore = db.createObjectStore('weightLogs', { keyPath: 'date' });
        weightStore.createIndex('by-date', 'date');
      }
      
      // Measurement logs store
      if (!db.objectStoreNames.contains('measurementLogs')) {
        const measureStore = db.createObjectStore('measurementLogs', { keyPath: 'id' });
        measureStore.createIndex('by-date', 'date');
      }
      
      // Progress photos store
      if (!db.objectStoreNames.contains('progressPhotos')) {
        const photoStore = db.createObjectStore('progressPhotos', { keyPath: 'id' });
        photoStore.createIndex('by-date', 'date');
      }
    },
  });
};

// Settings operations
export const getSettings = async () => {
  const db = await initDB();
  const settings = await db.get('settings', 'user-settings');
  return settings || getDefaultSettings();
};

export const saveSettings = async (settings) => {
  const db = await initDB();
  await db.put('settings', { ...settings, id: 'user-settings' });
};

export const getDefaultSettings = () => ({
  id: 'user-settings',
  startDate: new Date().toISOString().split('T')[0],
  startWeight: 110,
  targetWeight: 90,
  targetCalories: 1500,
  targetProtein: 180,
  targetWater: 3.5,
  durationDays: 60,
  darkMode: true,
});

// Daily log operations
export const getDailyLog = async (date) => {
  const db = await initDB();
  return db.get('dailyLogs', date);
};

export const saveDailyLog = async (log) => {
  const db = await initDB();
  await db.put('dailyLogs', log);
};

export const getAllDailyLogs = async () => {
  const db = await initDB();
  return db.getAll('dailyLogs');
};

// Workout operations
export const getWorkoutLog = async (id) => {
  const db = await initDB();
  return db.get('workoutLogs', id);
};

export const getWorkoutsByDate = async (date) => {
  const db = await initDB();
  const tx = db.transaction('workoutLogs', 'readonly');
  const index = tx.store.index('by-date');
  return index.getAll(date);
};

export const saveWorkoutLog = async (workout) => {
  const db = await initDB();
  await db.put('workoutLogs', workout);
};

export const getAllWorkoutLogs = async () => {
  const db = await initDB();
  return db.getAll('workoutLogs');
};

// Meal operations
export const getMealsByDate = async (date) => {
  const db = await initDB();
  const tx = db.transaction('mealLogs', 'readonly');
  const index = tx.store.index('by-date');
  return index.getAll(date);
};

export const saveMealLog = async (meal) => {
  const db = await initDB();
  await db.put('mealLogs', meal);
};

export const deleteMealLog = async (id) => {
  const db = await initDB();
  await db.delete('mealLogs', id);
};

export const getAllMealLogs = async () => {
  const db = await initDB();
  return db.getAll('mealLogs');
};

// Supplement operations
export const getSupplementsByDate = async (date) => {
  const db = await initDB();
  const tx = db.transaction('supplementLogs', 'readonly');
  const index = tx.store.index('by-date');
  return index.getAll(date);
};

export const saveSupplementLog = async (supp) => {
  const db = await initDB();
  await db.put('supplementLogs', supp);
};

export const getAllSupplementLogs = async () => {
  const db = await initDB();
  return db.getAll('supplementLogs');
};

// Weight operations
export const getWeightLog = async (date) => {
  const db = await initDB();
  return db.get('weightLogs', date);
};

export const saveWeightLog = async (weight) => {
  const db = await initDB();
  await db.put('weightLogs', weight);
};

export const getAllWeightLogs = async () => {
  const db = await initDB();
  return db.getAll('weightLogs');
};

// Measurement operations
export const getMeasurementsByDate = async (date) => {
  const db = await initDB();
  const tx = db.transaction('measurementLogs', 'readonly');
  const index = tx.store.index('by-date');
  return index.getAll(date);
};

export const saveMeasurementLog = async (measurement) => {
  const db = await initDB();
  await db.put('measurementLogs', measurement);
};

export const getAllMeasurementLogs = async () => {
  const db = await initDB();
  return db.getAll('measurementLogs');
};

// Progress photos operations
export const getPhotosByDate = async (date) => {
  const db = await initDB();
  const tx = db.transaction('progressPhotos', 'readonly');
  const index = tx.store.index('by-date');
  return index.getAll(date);
};

export const saveProgressPhoto = async (photo) => {
  const db = await initDB();
  await db.put('progressPhotos', photo);
};

export const deleteProgressPhoto = async (id) => {
  const db = await initDB();
  await db.delete('progressPhotos', id);
};

export const getAllProgressPhotos = async () => {
  const db = await initDB();
  return db.getAll('progressPhotos');
};

// Export all data
export const exportAllData = async () => {
  const db = await initDB();
  const data = {
    settings: await db.getAll('settings'),
    dailyLogs: await db.getAll('dailyLogs'),
    workoutLogs: await db.getAll('workoutLogs'),
    mealLogs: await db.getAll('mealLogs'),
    supplementLogs: await db.getAll('supplementLogs'),
    weightLogs: await db.getAll('weightLogs'),
    measurementLogs: await db.getAll('measurementLogs'),
    progressPhotos: await db.getAll('progressPhotos'),
  };
  return data;
};

// Import all data
export const importAllData = async (data) => {
  const db = await initDB();
  
  if (data.settings) {
    for (const item of data.settings) {
      await db.put('settings', item);
    }
  }
  if (data.dailyLogs) {
    for (const item of data.dailyLogs) {
      await db.put('dailyLogs', item);
    }
  }
  if (data.workoutLogs) {
    for (const item of data.workoutLogs) {
      await db.put('workoutLogs', item);
    }
  }
  if (data.mealLogs) {
    for (const item of data.mealLogs) {
      await db.put('mealLogs', item);
    }
  }
  if (data.supplementLogs) {
    for (const item of data.supplementLogs) {
      await db.put('supplementLogs', item);
    }
  }
  if (data.weightLogs) {
    for (const item of data.weightLogs) {
      await db.put('weightLogs', item);
    }
  }
  if (data.measurementLogs) {
    for (const item of data.measurementLogs) {
      await db.put('measurementLogs', item);
    }
  }
  if (data.progressPhotos) {
    for (const item of data.progressPhotos) {
      await db.put('progressPhotos', item);
    }
  }
};
